import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './therapies.css';

import imageAcupuncture from './images/acupucture.jpg';
import imageFrequency from './images/frequency.jpg';
import imageBiokybernetic from './images/biokybernetic.png';
import imageFood from './images/food.jpg';
import imageMassage from './images/massage.jpg';

interface Therapy {
    id: string;
    title: string;
    image?: string;
    imageStyle?: React.CSSProperties;
    label: string;
    featured?: boolean;
    summary: React.ReactNode;
    detail: React.ReactNode;
    ctaLabel?: string;
}

const THERAPIES: Therapy[] = [
    {
        id: 'sanza',
        title: 'SANZA Pro',
        label: 'Regeneration & Wellness',
        featured: true,
        summary: (
            <p>
                Ganzheitliche 16-Minuten-Anwendung mit PEMF, Bio-Frequenzen und Soft-Laser -
                f&uuml;r tiefe Entspannung, Energie und Regeneration.
            </p>
        ),
        detail: (
            <>
                <p>
                    In einer Zeit, in der Stress, Schlafmangel, Verspannungen und innere Unruhe f&uuml;r viele zum Alltag
                    geh&ouml;ren, suchen immer mehr Menschen nach sanften, nat&uuml;rlichen Wegen, wieder in Balance zu
                    kommen - ohne Pillen, ohne Nebenwirkungen, ohne gro&szlig;en Zeitaufwand.
                </p>
                <h4>Was macht SANZA so besonders?</h4>
                <p>SANZA kombiniert drei sanfte, sich erg&auml;nzende Technologien in einem Ger&auml;t:</p>
                <ul>
                    <li><strong>PEMF</strong> - tiefenwirksame Magnetfeldimpulse zur Unterst&uuml;tzung von Zellenergie, Durchblutung und Regulation.</li>
                    <li><strong>Bio-Frequenzen</strong> - individuell abgestimmte Frequenzmuster zur Harmonisierung k&ouml;rpereigener Prozesse.</li>
                    <li><strong>Soft-Laser</strong> - Photobiomodulation auf Zellebene zur F&ouml;rderung von Regeneration und Heilungsprozessen.</li>
                </ul>
                <p>
                    In nur 16 bis 20 Minuten pro Anwendung sp&uuml;ren viele Menschen tiefe Entspannung, besseren
                    Energiefluss, schnellere Erholung und oft schon nach wenigen Sitzungen ein deutlich gesteigertes
                    Wohlbefinden.
                </p>
                <h4>F&uuml;r wen eignet sich SANZA?</h4>
                <ul>
                    <li>Menschen mit chronischer Ersch&ouml;pfung, Schlafproblemen oder Burnout-Symptomen</li>
                    <li>Sportler und aktive Menschen mit erh&ouml;htem Regenerationsbedarf</li>
                    <li>Personen mit Verspannungen, Gelenk- oder R&uuml;ckenschmerzen als begleitende Ma&szlig;nahme</li>
                    <li>Alle, die pr&auml;ventiv in Zellgesundheit und innere Balance investieren m&ouml;chten</li>
                </ul>
                <h4>Wie l&auml;uft eine Anwendung ab?</h4>
                <ol>
                    <li>Kurze HRV-Messung zur Einordnung des aktuellen Regenerationszustands</li>
                    <li>Bequeme Lagerung im Sitzen oder Liegen</li>
                    <li>Sanfte Platzierung von Elektroden oder Pads an ausgew&auml;hlten Stellen</li>
                    <li>16 bis 30 Minuten ruhige Anwendung</li>
                    <li>Abschlie&szlig;ende HRV-Messung als Vorher-Nachher-Vergleich</li>
                </ol>
                <h4>Warum in der Naturheilpraxis?</h4>
                <p>
                    SANZA ist kein Ersatz f&uuml;r eine &auml;rztliche Behandlung, sondern eine erg&auml;nzende Methode innerhalb
                    eines ganzheitlichen Konzepts aus Ern&auml;hrung, Bewegung, Entspannung und naturheilkundlicher
                    Begleitung. Entwickelt wurde das System in 26 Jahren Forschung in Kooperation mit 12 Universit&auml;ten.
                </p>
            </>
        ),
        ctaLabel: 'Termin für SANZA buchen',
    },
    {
        id: 'hrv',
        title: 'HRV-Messung',
        image: imageFrequency,
        label: 'Analyse des Nervensystems',
        featured: true,
        summary: (
            <p>
                Wissenschaftlich fundierte Messung der Herzratenvariabilit&auml;t - zeigt Stresslevel,
                Regenerationsf&auml;higkeit und das biologische Alter des Nervensystems.
            </p>
        ),
        detail: (
            <>
                <p>
                    Viele Menschen sp&uuml;ren es: Der K&ouml;rper ist dauerhaft im Stressmodus, Schlaf wird unruhig,
                    Konzentration l&auml;sst nach und Erholung bleibt aus. Die HRV-Messung gibt einen schnellen,
                    nicht-invasiven und wissenschaftlich fundierten Blick auf genau diesen Zustand.
                </p>
                <h4>Was ist HRV eigentlich?</h4>
                <p>
                    Die Herzratenvariabilit&auml;t misst die feinen Schwankungen zwischen den einzelnen Herzschl&auml;gen.
                    Je gr&ouml;&szlig;er und harmonischer diese Schwankungen sind, desto besser ist die Anpassungsf&auml;higkeit
                    des vegetativen Nervensystems.
                </p>
                <ul>
                    <li><strong>Niedrige HRV:</strong> Hinweis auf chronischen Stress, geringe Regeneration und hohe Belastung</li>
                    <li><strong>Hohe HRV:</strong> gute Stress-Resilienz, schnelle Erholung und ausgeglichene Regulation</li>
                </ul>
                <h4>Wie l&auml;uft die Messung ab?</h4>
                <ol>
                    <li>Kurze Vorbereitung im Sitzen oder Liegen mit Brustgurt oder Fingerclip</li>
                    <li>Messung &uuml;ber etwa 5 bis 6 Minuten bei ruhiger, normaler Atmung</li>
                    <li>Sofortige Auswertung mit sichtbaren Werten zu Stress, Balance und Regeneration</li>
                </ol>
                <h4>Was zeigt die Auswertung konkret?</h4>
                <ul>
                    <li>aktuelles Stresslevel</li>
                    <li>biologisches Alter des Nervensystems</li>
                    <li>Balance zwischen Sympathikus und Parasympathikus</li>
                    <li>Regenerationsf&auml;higkeit und Koh&auml;renz</li>
                </ul>
                <h4>F&uuml;r wen ist die HRV-Messung sinnvoll?</h4>
                <ul>
                    <li>Menschen mit Ersch&ouml;pfung, Schlafproblemen oder innerer Unruhe</li>
                    <li>Sportler, die ihre Regeneration gezielt verbessern m&ouml;chten</li>
                    <li>Unternehmer, F&uuml;hrungskr&auml;fte und Selbstst&auml;ndige zur Burnout-Pr&auml;vention</li>
                    <li>Alle, die ihre Gesundheit pr&auml;ventiv besser verstehen wollen</li>
                </ul>
                <p>
                    In der Praxis betrachten wir die HRV nicht isoliert, sondern immer im Zusammenhang mit Ihrer
                    Lebenssituation, Ihren Beschwerden und Ihren Zielen.
                </p>
            </>
        ),
        ctaLabel: 'HRV-Termin buchen',
    },
    {
        id: 'phlebolyse',
        title: 'Hautanomalien (Phlebolyse)',
        label: 'Sanfte Hautbehandlung',
        featured: true,
        summary: (
            <p>
                Sanfte, narbenfreie Entfernung von Warzen, Altersflecken, &Auml;derchen und weiteren
                Hautver&auml;nderungen mittels Phlebolyser&reg; - nach individueller Abkl&auml;rung.
            </p>
        ),
        detail: (
            <>
                <p>
                    Altersflecken, Fibrome, Angiome, Besenreiser, Couperose, Warzen und mehr k&ouml;nnen nicht nur
                    optisch, sondern auch emotional belastend sein. Die Phlebolyse bietet hier eine sanfte,
                    minimal-invasive Alternative zu Vereisung, Laser oder chirurgischem Entfernen.
                </p>
                <h4>Wie funktioniert die Phlebolyse?</h4>
                <p>
                    Die Behandlung nutzt hochfrequente Radiowellen, die &uuml;ber eine sehr feine Goldsonde gezielt in das
                    Gewebe eingebracht werden. Dadurch wird die Hautver&auml;nderung lokal ver&ouml;det, w&auml;hrend das umliegende
                    Gewebe weitgehend geschont bleibt.
                </p>
                <h4>H&auml;ufig behandelte Indikationen</h4>
                <ul>
                    <li>Warzen, Dornwarzen und Feigwarzen</li>
                    <li>Fibrome und Stielwarzen</li>
                    <li>Altersflecken und aktinische Keratosen</li>
                    <li>Besenreiser, Teleangiektasien und Couperose</li>
                    <li>Angiome, Xanthelasmen und seborrhoische Keratosen</li>
                    <li>Herpesbl&auml;schen und kleine entz&uuml;ndliche Knoten</li>
                </ul>
                <h4>Typischer Verlauf</h4>
                <ul>
                    <li>Behandlungsdauer meist 5 bis 20 Minuten</li>
                    <li>Abheilung in der Regel innerhalb von 3 bis 14 Tagen</li>
                    <li>Gr&ouml;&szlig;ere oder tiefere Ver&auml;nderungen k&ouml;nnen mehrere Sitzungen erfordern</li>
                </ul>
                <h4>Vorteile</h4>
                <ul>
                    <li>keine Bet&auml;ubungsspritze n&ouml;tig</li>
                    <li>sehr geringe Narbenbildung bei korrekter Nachsorge</li>
                    <li>kurze Sitzungen und meist kaum Ausfallzeit</li>
                    <li>auch an empfindlichen Stellen gut einsetzbar</li>
                </ul>
                <h4>Vor- und Nachsorge</h4>
                <p>
                    Vor der Behandlung sollten keine Selbstbr&auml;uner verwendet werden. Danach gilt: Krusten nicht
                    entfernen, Sonnenschutz mit hohem Lichtschutzfaktor verwenden und f&uuml;r einige Tage auf Sauna,
                    Solarium und Schwimmbad verzichten.
                </p>
            </>
        ),
        ctaLabel: 'Beratungstermin buchen',
    },
    {
        id: 'tcm',
        title: 'Traditionelle Chinesische Medizin',
        image: imageAcupuncture,
        label: 'Ganzheitliche Begleitung',
        summary: <p>Wird h&auml;ufig begleitend eingesetzt, um Balance und Wohlbefinden zu f&ouml;rdern.</p>,
        detail: (
            <p>
                Die Traditionelle Chinesische Medizin wird in meiner Praxis als ganzheitliche Begleitung eingesetzt,
                wenn es darum geht, Regulation, Balance und Wohlbefinden zu unterst&uuml;tzen. Welche Elemente sinnvoll
                sind, kl&auml;ren wir im Erstgespr&auml;ch gemeinsam anhand Ihrer Beschwerden und Ziele.
            </p>
        ),
    },
    {
        id: 'frequenz',
        title: 'Wellanalyse (Frequenztherapie)',
        image: imageFrequency,
        label: 'Sanfte Impulse',
        summary: <p>Sanfte, individuell abgestimmte Impulse zur Unterst&uuml;tzung der Regulation.</p>,
        detail: (
            <p>
                Die Frequenztherapie arbeitet mit sanften, individuell ausgew&auml;hlten Impulsen. Sie kann eingesetzt
                werden, um Regulationsprozesse zu begleiten und das Gesamtkonzept Ihrer Behandlung zu erg&auml;nzen.
            </p>
        ),
    },
    {
        id: 'biokybernetic',
        title: 'Biokybernetik nach Smit',
        image: imageBiokybernetic,
        imageStyle: { backgroundSize: '90%' },
        label: 'Selbstregulation',
        summary: <p>Zur Unterst&uuml;tzung von Selbstregulation und Reizverarbeitung.</p>,
        detail: (
            <p>
                Die Biokybernetik nach Smit wird eingesetzt, um Selbstregulation und Reizverarbeitung zu unterst&uuml;tzen.
                Je nach Befund kann sie eine sinnvolle erg&auml;nzende Methode innerhalb des Therapieplans sein.
            </p>
        ),
    },
    {
        id: 'ernaehrung',
        title: 'Ernährungsberatung',
        image: imageFood,
        label: 'Alltagstaugliche Empfehlungen',
        summary: <p>Empfehlungen, die zu Ihren Zielen, Beschwerden und Routinen passen.</p>,
        detail: (
            <p>
                In der Ernährungsberatung geht es um umsetzbare Empfehlungen für Ihren Alltag. Statt starrer Pläne
                entwickeln wir gemeinsam eine Ernährungsweise, die zu Ihren Zielen, Beschwerden und Gewohnheiten passt.
            </p>
        ),
    },
    {
        id: 'massage',
        title: 'Massage',
        image: imageMassage,
        label: 'Entspannung & Lockerung',
        summary: <p>Zur Entspannung, Lockerung und wohltuenden Unterst&uuml;tzung.</p>,
        detail: (
            <p>
                Massagen dienen in meiner Praxis dazu, Spannungen zu lösen, Gewebe zu lockern und die Behandlung
                angenehm zu ergänzen. Sie werden immer passend zum individuellen Beschwerdebild ausgewählt.
            </p>
        ),
    },
];

const THERAPY_IDS = new Set(THERAPIES.map((therapy) => therapy.id));

const Therapies = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTherapy = searchParams.get('therapy');
    const initialTherapy = requestedTherapy && THERAPY_IDS.has(requestedTherapy) ? requestedTherapy : 'sanza';
    const [selectedId, setSelectedId] = useState(initialTherapy);

    useEffect(() => {
        if (!requestedTherapy || !THERAPY_IDS.has(requestedTherapy)) return;
        setSelectedId(requestedTherapy);
    }, [requestedTherapy]);

    const selectTherapy = (therapyId: string) => {
        setSelectedId(therapyId);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('therapy', therapyId);
        setSearchParams(nextParams, { replace: true });
    };

    const selectedTherapy = useMemo(
        () => THERAPIES.find((therapy) => therapy.id === selectedId) ?? THERAPIES[0],
        [selectedId],
    );

    return (
        <div className="section sectionBackground" id="therapies">
            <div className="viewport therapiesViewport">
                <div className="therapiesIntro">
                    <h1>Therapien</h1>
                    <p className="sectionLead">
                        Diese Methoden setze ich ein, um Sie bei Beschwerden zu begleiten. W&auml;hlen Sie eine Methode aus,
                        um rechts die Beschreibung und weitere Details zu sehen.
                    </p>
                </div>
                <div className="therapiesLayout">
                    <div className="therapySelectorPane" aria-label="Therapien ausw&auml;hlen">
                        <div className="therapySelectorGrid">
                            {THERAPIES.map((therapy) => (
                                <button
                                    key={therapy.id}
                                    type="button"
                                    className={`therapySelectorCard${therapy.id === selectedTherapy.id ? ' therapySelectorCardActive' : ''}${therapy.featured ? ' therapySelectorCardFeatured' : ''}`}
                                    onClick={() => selectTherapy(therapy.id)}
                                    aria-pressed={therapy.id === selectedTherapy.id}
                                >
                                    <div
                                        className="therapySelectorImage"
                                        style={therapy.image ? { backgroundImage: `url(${therapy.image})`, ...therapy.imageStyle } : therapy.imageStyle}
                                    />
                                    <div className="therapySelectorText">
                                        <div className="therapySelectorMetaRow">
                                            <span className="therapySelectorLabel">{therapy.label}</span>
                                            {therapy.featured && <span className="therapySelectorBadge">Schwerpunkt</span>}
                                        </div>
                                        <h3>{therapy.title}</h3>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="therapyDetailPane" aria-live="polite">
                        <div className="therapyDetailHeader">
                            <div
                                className="therapyDetailImage"
                                style={selectedTherapy.image ? { backgroundImage: `url(${selectedTherapy.image})`, ...selectedTherapy.imageStyle } : selectedTherapy.imageStyle}
                            />
                            <div className="therapyDetailIntro">
                                <span className="therapyDetailLabel">{selectedTherapy.label}</span>
                                <h2>{selectedTherapy.title}</h2>
                                <div className="therapyDetailSummary">{selectedTherapy.summary}</div>
                            </div>
                        </div>
                        <div className="therapyDetailContent">
                            {selectedTherapy.detail}
                            {selectedTherapy.ctaLabel && (
                                <a className="itemDetailCta" href="#page-booking">{selectedTherapy.ctaLabel}</a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Therapies;